import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback
} from 'react';
import {
  Container,
  Grid,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import _get from 'lodash/get';
import _capitalize from 'lodash/capitalize';
import moment from 'moment';

import {
  UserContext,
  UserDispatchContext
} from '../../../Contexts/UserContext';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';

import Title from '../../../Components/Labels/Title';
import SweetAlert from '../../../Components/Alerts/SweetAlert';
import ModalProgress from '../../../Components/Progress/Modal/ModalProgress';
import BaseButton from '../../../Components/Buttons/BaseButton';
import RequestType from '../Utils/RequestType';
import RequestStatus from '../Utils/RequestStatus';
import RequestIcon from '../Utils/RequestIcon';
import RequestInfo from '../Utils/RequestInfo';

import {
  ApproveRequestAPI,
  GetRequestAPI,
  RejectRequestAPI
} from '../../../API/Requests/requestsAPI';
import {
  ContractAssociationRequestTypes,
  renderStatus,
  requestStatus
} from '../Utils/enums';
import { history } from '../../../Routes/history';
import {
  extractErrorMessage,
  redirectOnAuthFailure
} from '../../../Utils/Errors/Errors';
import LoginDialog from '../../Login/LoginDialog';
import { logoutUser } from '../../../Utils/User/Actions';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import { ALERT_TYPE } from '../../../Components/Alerts/alert_enums';
import ConfirmationDialog from '../../../Components/Dialogs/ConfirmationDialog';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import TextAreaInput from '../../../Components/Inputs/TextAreaInput';
import { ContractsRefreshContext } from '../../../Contexts/ContractsContext';
import { yupResolver } from '@hookform/resolvers/yup';

const validationSchema = yup
  .object({
    reason: yup
      .string()
      .min(10, 'Ingresa al menos 10 caracteres')
      .max(200, 'Máximo 200 caracteres')
      .trim()
      .required('Ingresa una razón de rechazo')
  })
  .required();

const ManageRequest = ({ match }) => {
  const classes = useStyles();
  const mediumScreen = useMediaQuery(theme => theme.breakpoints.down(750));
  const requestId = match.params.request;

  const mounted = useRef(true);

  // * CONTEXTS
  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);
  const setAlert = useContext(AlertsDispatchContext);
  const refreshContracts = useContext(ContractsRefreshContext);

  const authToken = _get(currentUser, 'token');

  // * STATE HOOKS
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState({});
  const [approveConfirmationDialog, setApproveConfirmationDialog] = useState(
    false
  );
  const [rejectConfirmationDialog, setRejectConfirmationDialog] = useState(
    false
  );

  const {
    register,
    errors,
    formState: { isValid },
    getValues
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { reason: '' }
  });

  const {
    id,
    contractId,
    type,
    data,
    status,
    createdAt,
    inProcessAt,
    canceledAt,
    attendedAt
  } = request;

  useEffect(() => {
    const getRequest = async () => {
      setLoading(true);
      const response = await GetRequestAPI(authToken, requestId, {
        forApprover: true
      });

      if (response.success) {
        const requestData = response.data.data.data.request;
        if (!ContractAssociationRequestTypes.includes(requestData.type)) {
          setAlert({
            type: ALERT_TYPE.ERROR,
            message: 'No se permite gestionar este tipo de solicitud'
          });
          history.push(`${ROUTE_NAMES.requests}/${requestData.id}`);
        }

        setRequest(requestData);
      } else {
        history.replace('/solicitudes');
        mounted.current = false;
        setAlert({
          type: ALERT_TYPE.ERROR,
          message: extractErrorMessage(response).message
        });
      }

      if (mounted.current) {
        setLoading(false);
      }
    };

    if (!authToken) {
      return;
    }

    getRequest();
  }, [authToken, requestId, setAlert]);

  const getSteps = () => {
    return [
      {
        text: renderStatus(requestStatus.registered).statusText,
        date: createdAt && moment(createdAt).format('DD/MM/YYYY'),
        completed: true
      },
      status === requestStatus.cancelled
        ? {
            text: renderStatus(requestStatus.cancelled).statusText,
            date: canceledAt && moment(canceledAt).format('DD/MM/YYYY'),
            completed: true,
            error: true,
            icon: <CancelIcon color="error" />
          }
        : {
            text: renderStatus(requestStatus.inProcess).statusText,
            date:
              (status === requestStatus.inProcess ||
                status === requestStatus.attended) &&
              inProcessAt &&
              moment(inProcessAt).format('DD/MM/YYYY'),
            completed:
              status === requestStatus.inProcess ||
              status === requestStatus.attended
          },
      {
        text: renderStatus(requestStatus.attended).statusText,
        date:
          status === requestStatus.attended &&
          attendedAt &&
          moment(attendedAt).format('DD/MM/YYYY'),
        completed: status === requestStatus.attended
      }
    ];
  };

  const steps = request && getSteps();

  const handleCloseLoginDialog = useCallback(() => {
    history.replace('/');
  }, []);

  const handleApproveConfirmationDialog = () => {
    setApproveConfirmationDialog(true);
  };

  const handleRejectConfirmationDialog = () => {
    setRejectConfirmationDialog(true);
  };

  const handleApprovalRequest = async close => {
    if (close) {
      return { closeDialog: true };
    }

    setLoading(true);
    const response = await ApproveRequestAPI(authToken, requestId);

    setApproveConfirmationDialog(false);

    if (response.success) {
      const requestData = response.data.data.data.request;
      setRequest(requestData);
      setLoading(false);
      setAlert({
        type: ALERT_TYPE.SUCCESS,
        message: `La solicitud #${requestData.id} fue aprobada`
      });
      refreshContracts();
      return { closeDialog: true };
    }

    if (
      redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
    ) {
      return { unmounting: true };
    }

    setAlert({
      type: ALERT_TYPE.ERROR,
      message: extractErrorMessage(response).message
    });
    setLoading(false);

    return { closeDialog: true };
  };

  const handleRejectRequest = async close => {
    if (close) {
      return { closeDialog: true };
    }

    setLoading(true);
    const response = await RejectRequestAPI(authToken, requestId, {
      ...getValues()
    });

    setApproveConfirmationDialog(false);

    if (response.success) {
      const requestData = response.data.data.data.request;
      setRequest(requestData);
      setLoading(false);
      setAlert({
        type: ALERT_TYPE.SUCCESS,
        message: `La solicitud #${requestData.id} fue rechazada`
      });

      return { unmounting: false, closeDialog: true };
    }

    if (
      redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
    ) {
      return { unmounting: true };
    }

    setAlert({
      type: ALERT_TYPE.ERROR,
      message: extractErrorMessage(response).message
    });
    setLoading(false);

    return { unmounting: false, closeDialog: true };
  };

  const renderApproveDialogContent = (
    <div>
      <Typography>
        A continuación se <span className={classes.approvedText}>aprobará</span>{' '}
        la solicitud #{request.id}, ¿está seguro?
      </Typography>

      {ContractAssociationRequestTypes.includes(request.type) && (
        <SweetAlert
          type="info"
          noIcon={mediumScreen}
          classes={{ root: classes.warningAlert }}
          message={
            <>
              Al aprobar la solicitud se asociará el contrato al usuario
              solicitante.
            </>
          }
        />
      )}
    </div>
  );

  const renderRejectDialogContent = (
    <>
      <Typography>Ingrese una razón de rechazo:</Typography>
      <TextAreaInput
        label="Razón de rechazo*"
        name="reason"
        ref={register}
        error={Boolean(errors.reason)}
        helperText={errors.reason && errors.reason.message}
        fullWidth
        maxLength={200}
        disabled={loading}
        margin="normal"
      />
      <Typography>
        A continuación se{' '}
        <span className={classes.rejectedText}>rechazará</span> la solicitud #
        {request.id}, ¿está seguro?
      </Typography>
      <SweetAlert
        type="warning"
        noIcon={mediumScreen}
        classes={{ root: classes.warningAlert }}
        message={
          <>
            Tenga en cuenta que la razón de rechazo será visible para el usuario
            que realizó la solicitud.
          </>
        }
      />
    </>
  );

  if (!currentUser) {
    return <LoginDialog open={true} setOpen={handleCloseLoginDialog} />;
  }

  return (
    <>
      <ConfirmationDialog
        open={approveConfirmationDialog}
        setDialog={setApproveConfirmationDialog}
        requestCallback={handleApprovalRequest}
        title="Aprobar solicitud"
        content={renderApproveDialogContent}
        disabled={loading}
        cancelButtonText="Cancelar"
        submitButtonText="Confirmar"
      />
      <ConfirmationDialog
        open={rejectConfirmationDialog}
        setDialog={setRejectConfirmationDialog}
        requestCallback={handleRejectRequest}
        title="Rechazar solicitud"
        content={renderRejectDialogContent}
        disabled={loading || !isValid}
        cancelButtonText="Cancelar"
        submitButtonText="Confirmar"
      />
      <div className={classes.root}>
        <Container maxWidth="md" className={classes.innerRoot}>
          {loading ? (
            <ModalProgress />
          ) : (
            <>
              <Title className={classes.title} title="Gestionar solicitud" />
              <div className={classes.requestContainer}>
                <Grid
                  container
                  wrap="nowrap"
                  alignItems="center"
                  className={classes.headerContainer}
                >
                  <Grid item>
                    <Grid container alignItems="center">
                      {mediumScreen && (
                        <RequestIcon className={classes.typeIcon} type={type} />
                      )}
                    </Grid>
                  </Grid>

                  <Grid item className={classes.headerInfo}>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item className={classes.headerInfoColumn}>
                        <Grid container alignItems="center">
                          <RequestType type={type} withIcon={!mediumScreen} />
                        </Grid>
                      </Grid>

                      <Grid
                        item
                        container
                        justifyContent={mediumScreen ? 'flex-start' : 'center'}
                        className={classes.headerInfoColumn}
                      >
                        <Typography>Solicitud nº {id}</Typography>
                      </Grid>

                      <Grid
                        item
                        container
                        justifyContent={
                          mediumScreen ? 'flex-start' : 'flex-end'
                        }
                        className={classes.headerInfoColumn}
                      >
                        <Grid item>
                          <RequestStatus status={status} withLabel={false} />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <div className={classes.stepperContainer}>
                  <Stepper
                    classes={{
                      root: classes.stepper
                    }}
                    alternativeLabel
                  >
                    {steps.map(step => (
                      <Step
                        key={step.text}
                        classes={{ horizontal: classes.horizontal }}
                        completed={step.completed}
                      >
                        <StepLabel
                          error={step.error}
                          StepIconProps={{
                            classes: {
                              root: classes.stepIcon
                            }
                          }}
                          classes={{
                            labelContainer: classes.alternativeLabel,
                            label: classes.label
                          }}
                        >
                          {step.text}
                          <div className={classes.stepDate}>{step.date}</div>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>

                  {status === requestStatus.cancelled &&
                    data.output &&
                    (data.output.failureReason ||
                      data.output.causalDescription) && (
                      <Grid container justifyContent="center">
                        <Grid item>
                          <SweetAlert
                            noIcon
                            id="Failed_request_warning"
                            type="error"
                            classes={{
                              root: classes.sweetAlert
                            }}
                            message={
                              <>
                                <strong>Causal de rechazo:</strong>{' '}
                                {_capitalize(
                                  data.output.failureReason ||
                                    data.output.causalDescription
                                )}
                              </>
                            }
                          />
                        </Grid>
                      </Grid>
                    )}
                </div>

                <RequestInfo
                  contractId={contractId}
                  data={data}
                  type={type}
                  status={status}
                />
              </div>

              {[requestStatus.registered, requestStatus.inProcess].includes(
                status
              ) && (
                <Grid container justifyContent="flex-end" spacing={1}>
                  <Grid item>
                    <BaseButton
                      onClick={handleRejectConfirmationDialog}
                      className={classes.button}
                      size="small"
                      variant="outlined"
                      loading={loading}
                    >
                      Rechazar
                    </BaseButton>
                  </Grid>
                  <Grid item>
                    <BaseButton
                      onClick={handleApproveConfirmationDialog}
                      className={classes.button}
                      size="small"
                      loading={loading}
                    >
                      Aprobar
                    </BaseButton>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Container>
      </div>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0
    },
    '100%': {
      opacity: 1
    }
  },
  root: {
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  innerRoot: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: theme.spacing(4),
    paddingTop: theme.spacing(4),
    fontSize: 16,
    [theme.breakpoints.down(750)]: {
      fontSize: 15
    },
    [theme.breakpoints.down(450)]: {
      fontSize: 14
    }
  },
  backButton: {
    fontSize: '1em',
    alignSelf: 'flex-start',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up(750)]: {
      marginTop: theme.spacing(5)
    }
  },
  title: { margin: 0, marginBottom: theme.spacing(2) },
  backIcon: {
    marginRight: theme.spacing()
  },
  requestContainer: {
    animation: `$fadeIn 250ms ${theme.transitions.easing.easeInOut}`,
    backgroundColor: theme.palette.background.cardDark,
    borderRadius: theme.spacing(0.75),
    padding: '1em',
    border: `1px solid ${theme.palette.background.border}`,
    marginBottom: '0.5em',
    '&:last-of-type': {
      marginBottom: 0
    }
  },
  headerContainer: {
    paddingBottom: '1em',
    borderBottom: `1px solid ${theme.palette.background.border}`,
    [theme.breakpoints.up(750)]: {
      paddingLeft: '1em',
      paddingRight: '1em'
    }
  },
  headerInfo: {
    flexGrow: 1
  },
  typeIcon: {
    [theme.breakpoints.down(750)]: {
      width: '2.5em',
      height: '2.5em'
    }
  },
  headerInfoColumn: {
    [theme.breakpoints.up(750)]: {
      width: 'calc(4 / 12 * 100%)'
    }
  },
  // Stepper classes
  stepperContainer: {
    borderBottom: `1px solid ${theme.palette.background.border}`,
    marginBottom: '0.8em'
  },
  stepper: {
    backgroundColor: 'transparent',
    padding: '1em 0'
  },
  stepIcon: {
    width: '0.9em',
    height: '0.9em',
    [theme.breakpoints.down(450)]: {
      width: '0.7em',
      height: '0.7em'
    }
  },
  label: {
    fontWeight: `${theme.typography.fontWeightRegular} !important`,
    marginTop: '0.5em !important',
    fontSize: '0.9em',
    [theme.breakpoints.down(450)]: {
      fontSize: '0.8em'
    }
  },
  horizontal: {
    padding: 0
  },
  stepDate: {
    fontSize: '0.8em'
  },
  sweetAlert: {
    margin: '0 auto 1em',
    display: 'inline-block',
    paddingLeft: '1em',
    paddingRight: '1em'
  },
  button: {
    marginTop: theme.spacing(2),
    alignSelf: 'flex-end',
    [theme.breakpoints.down(450)]: {
      width: '100%'
    }
  },
  dialogContent: {
    minHeight: 'unset'
  },
  warningAlert: {
    marginTop: theme.spacing(1)
  },
  rejectedText: {
    color: theme.palette.color.danger
  },
  approvedText: {
    color: theme.palette.color.primary
  }
}));

export default ManageRequest;
